/**
 * Utilities for converting plain text to ProseMirror documents.
 *
 * Supports multiple strategies:
 * 1. Single paragraph (default for replay) - entire text as one paragraph
 * 2. Lines - split on \n, create paragraph per line
 * 3. Paragraphs - split on \n\n (existing behavior in createDocFromText)
 *
 * The "single" strategy is recommended for replay rendering because:
 * - Simple position mapping: textPos + 1 = pmPos (no boundary calculations)
 * - Matches current behavior: Current HTML renderer uses whitespace-pre-wrap
 * - Fast: No text splitting or iteration
 * - Predictable: No edge cases around empty paragraphs or trailing newlines
 */

import type { Node as ProseMirrorNode, Schema } from "@tiptap/pm/model";

export type TextConversionStrategy = "single" | "lines" | "paragraphs";

export interface TextToProseMirrorOptions {
  /** Conversion strategy (default: "single" for replay compatibility) */
  strategy?: TextConversionStrategy;
  /** Preserve empty paragraphs (default: false) */
  preserveEmpty?: boolean;
}

/**
 * Create a document with a single paragraph containing all text.
 * Newlines are preserved as literal \n characters in the text node.
 * Use CSS whitespace-pre-wrap for display.
 *
 * Position mapping is trivial: pmPos = textPos + 1
 */
function createSingleParagraph(text: string, schema: Schema): ProseMirrorNode {
  if (!text) {
    return schema.node("doc", null, [schema.node("paragraph")]);
  }
  const paragraph = schema.node("paragraph", null, [schema.text(text)]);
  return schema.node("doc", null, [paragraph]);
}

/**
 * Create a document with one paragraph per line (split on single \n).
 * Position mapping must account for paragraph boundaries.
 */
function createFromLines(
  text: string,
  schema: Schema,
  preserveEmpty: boolean
): ProseMirrorNode {
  if (!text) {
    return schema.node("doc", null, [schema.node("paragraph")]);
  }

  const lines = text.split("\n");
  const paragraphs: ProseMirrorNode[] = [];

  for (const line of lines) {
    if (line || preserveEmpty) {
      const content = line ? [schema.text(line)] : [];
      paragraphs.push(schema.node("paragraph", null, content));
    }
  }

  // If all lines were empty and preserveEmpty is false, return empty paragraph
  if (paragraphs.length === 0) {
    return schema.node("doc", null, [schema.node("paragraph")]);
  }

  return schema.node("doc", null, paragraphs);
}

/**
 * Create a document with one paragraph per double-newline-separated block.
 * This matches the existing behavior of createDocFromText() in schema.ts.
 */
function createFromParagraphs(
  text: string,
  schema: Schema,
  preserveEmpty: boolean
): ProseMirrorNode {
  if (!text) {
    return schema.node("doc", null, [schema.node("paragraph")]);
  }

  const blocks = text.split(/\n\n+/);
  const paragraphs: ProseMirrorNode[] = [];

  for (const block of blocks) {
    const trimmed = block.trim();
    if (trimmed || preserveEmpty) {
      const content = trimmed ? [schema.text(trimmed)] : [];
      paragraphs.push(schema.node("paragraph", null, content));
    }
  }

  // If all blocks were empty and preserveEmpty is false, return empty paragraph
  if (paragraphs.length === 0) {
    return schema.node("doc", null, [schema.node("paragraph")]);
  }

  return schema.node("doc", null, paragraphs);
}

/**
 * Convert plain text to ProseMirror document.
 *
 * Default strategy is "single" paragraph to ensure position mapping
 * is straightforward (text position = PM position - 1 for paragraph start).
 *
 * @param text - The plain text to convert
 * @param schema - The ProseMirror schema to use (must have "doc", "paragraph", "text" nodes)
 * @param options - Conversion options
 * @returns A ProseMirror document node
 *
 * @example
 * ```typescript
 * // Single paragraph (default) - best for replay
 * const doc = textToProseMirror("Hello\nWorld", schema);
 * // Result: doc > paragraph > "Hello\nWorld"
 *
 * // Lines strategy - one paragraph per line
 * const doc = textToProseMirror("Line 1\nLine 2", schema, { strategy: "lines" });
 * // Result: doc > paragraph > "Line 1", paragraph > "Line 2"
 *
 * // Paragraphs strategy - split on double newlines
 * const doc = textToProseMirror("Para 1\n\nPara 2", schema, { strategy: "paragraphs" });
 * // Result: doc > paragraph > "Para 1", paragraph > "Para 2"
 * ```
 */
export function textToProseMirror(
  text: string,
  schema: Schema,
  options?: TextToProseMirrorOptions
): ProseMirrorNode {
  const strategy = options?.strategy ?? "single";
  const preserveEmpty = options?.preserveEmpty ?? false;

  switch (strategy) {
    case "single":
      return createSingleParagraph(text, schema);
    case "lines":
      return createFromLines(text, schema, preserveEmpty);
    case "paragraphs":
      return createFromParagraphs(text, schema, preserveEmpty);
    default: {
      // Exhaustive check - TypeScript will error if we miss a case
      const exhaustiveCheck: never = strategy;
      throw new Error(`Unknown strategy: ${String(exhaustiveCheck)}`);
    }
  }
}
